import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ItineraryCard from "@/components/ItineraryCard";
import { api } from "@/lib/api";

export default function CreatorProfile() {
  const { user_id } = useParams();
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/creators/${user_id}`)
      .then((r) => { setCreator(r.data); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [user_id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--paper)]">
        <Navbar />
        <div className="max-w-[1280px] mx-auto px-6 sm:px-10 py-20 text-center mono text-[11px] tracking-[0.2em] uppercase text-[var(--ink3)]">◆ Loading…</div>
      </div>
    );
  }
  if (notFound || !creator) {
    return (
      <div className="min-h-screen bg-[var(--paper)]">
        <Navbar />
        <div className="max-w-[800px] mx-auto px-6 py-24 text-center">
          <span className="eyebrow">◆ Not found</span>
          <h1 className="h-display text-[40px] mt-3">This creator doesn't exist.</h1>
          <Link to="/explore" className="btn btn-solid mt-6 inline-flex">Browse marketplace →</Link>
        </div>
      </div>
    );
  }

  const initials = (creator.name || "??").split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");
  const socials = [
    creator.instagram_handle && ["Instagram", creator.instagram_handle],
    creator.youtube_handle && ["YouTube", creator.youtube_handle],
    creator.tiktok_handle && ["TikTok", creator.tiktok_handle],
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-[var(--paper)]" data-testid="creator-profile">
      <Navbar />

      <section className="max-w-[1280px] mx-auto px-6 sm:px-10 pt-12 pb-10">
        <span className="eyebrow">◆ Verified creator</span>

        <div className="mt-6 grid gap-8 md:grid-cols-[auto_1fr] items-start">
          {creator.avatar_url ? (
            <img
              src={creator.avatar_url}
              alt={creator.name}
              className="w-[120px] h-[120px] rounded-full object-cover border-[1.5px] border-[var(--line)]"
              data-testid="creator-avatar"
            />
          ) : (
            <div
              className="w-[120px] h-[120px] rounded-full bg-[var(--ink)] text-white grid place-items-center serif text-[44px]"
              data-testid="creator-avatar"
            >{initials}</div>
          )}

          <div>
            <h1 className="h-display text-[44px] sm:text-[56px] leading-[1]">{creator.name}</h1>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="tag" style={{ color: "var(--red)", borderColor: "var(--red)" }}>Verified</span>
              {socials.map(([net, handle]) => (
                <span key={net} className="tag">{net}: {handle}</span>
              ))}
              <span className="tag">{creator.total_sales} sales</span>
            </div>
            {creator.bio && (
              <p className="mt-5 text-[15px] leading-[1.7] text-[var(--ink2)] max-w-[640px]">{creator.bio}</p>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-[1280px] mx-auto px-6 sm:px-10 pb-20">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <span className="eyebrow">◆ Live routes</span>
            <h2 className="h-display text-[32px] mt-2">
              {(creator.itineraries ?? []).length} {(creator.itineraries ?? []).length === 1 ? "route" : "routes"} by {creator.name?.split(" ")[0]}
            </h2>
          </div>
        </div>

        {(creator.itineraries ?? []).length === 0 ? (
          <div className="bg-[var(--card)] border-[1.5px] border-dashed border-[var(--line)] rounded-[18px] py-14 text-center text-[14px] text-[var(--ink3)]">
            No live routes yet.
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="creator-itineraries">
            {(creator.itineraries ?? []).map((it) => (
              <ItineraryCard key={it.itinerary_id} itin={it} />
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
